import {
  ChangeEvent,
  DragEvent,
  FC,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faCamera,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslations } from 'next-intl';
import { getImageProps } from 'next/image';
import type { User as FirebaseUser } from 'firebase/auth';
import GoogleLogo from 'assets/icons/icon-google.svg';
import FacebookLogo from 'assets/icons/icon-facebook.svg';
import { closeModal } from 'xstate/actors/ModalActor';
import { useAuthentication } from 'xstate/hooks/useAuthentication';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import {
  ProfileImageSource,
  useSetProfileImage,
} from 'networking/mindfeed/hooks/useSetProfileImage';
import { UnsupportedImageError } from 'utils/ImageUtils';
import Avatar, { MediaAvatar } from 'elements/Avatar';
import Spinner from 'elements/Spinner';

type ProviderId = 'google.com' | 'facebook.com';

export type ProviderPhoto = {
  providerId: ProviderId;
  name: string;
  url: string;
};

const PROVIDER_NAMES: Record<ProviderId, string> = {
  'google.com': 'Google',
  'facebook.com': 'Facebook',
};

const PROVIDER_LOGOS: Record<ProviderId, FC<{ className?: string }>> = {
  'google.com': GoogleLogo,
  'facebook.com': FacebookLogo,
};

// Which sign-in a photo address belongs to, for when Firebase gives the
// photo without saying where it came from.
const providerOfPhoto = (url: string): ProviderId | undefined => {
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return undefined;
  }
  if (host.endsWith('googleusercontent.com')) {
    return 'google.com';
  }
  if (/(^|\.)(facebook\.com|fbcdn\.net|fbsbx\.com)$/.test(host)) {
    return 'facebook.com';
  }
  return undefined;
};

// Google and Facebook give a small photo by default; ask for a bigger one.
const largePhotoUrl = (providerId: ProviderId, url: string): string => {
  if (providerId === 'google.com') {
    return url.replace(/=s\d+-c$/, '=s400-c');
  }
  const large = new URL(url);
  large.searchParams.set('type', 'large');
  return large.toString();
};

// The photo through our image optimiser, on our own site. Google refuses
// (429) many photos loaded straight from a browser, and this also lets us
// download it to upload.
const optimisedPhotoUrl = (url: string): string =>
  getImageProps({ src: url, alt: '', width: 400, height: 400 }).props.src;

// The photos of the Google and Facebook accounts the user signs in with.
// Each sign-in usually carries its own photo; some accounts only have the
// main one, so that is used too when we can tell where it came from.
export const providerPhotos = (
  firebaseUser: FirebaseUser | null,
): ProviderPhoto[] => {
  if (!firebaseUser) {
    return [];
  }

  const candidates = [
    ...(firebaseUser.providerData ?? []).map(({ providerId, photoURL }) => ({
      providerId,
      photoURL,
    })),
    {
      providerId: firebaseUser.photoURL
        ? providerOfPhoto(firebaseUser.photoURL)
        : undefined,
      photoURL: firebaseUser.photoURL,
    },
  ];

  const photos = new Map<ProviderId, ProviderPhoto>();
  candidates.forEach(({ providerId, photoURL }) => {
    if (!photoURL || !(providerId && providerId in PROVIDER_NAMES)) {
      return;
    }
    const id = providerId as ProviderId;
    if (photos.has(id)) {
      return;
    }
    photos.set(id, {
      providerId: id,
      name: PROVIDER_NAMES[id],
      url: largePhotoUrl(id, photoURL),
    });
  });
  return [...photos.values()];
};

// Add, change or remove the profile photo: use the photo from a Google or
// Facebook sign-in, upload a file, or go back to the initials.
const ProfilePhotoModal = (): ReactElement | null => {
  const t = useTranslations();
  const { authenticatedUser: user } = useAuthenticatedUser();
  const { firebaseUser } = useAuthentication();
  const setPhoto = useSetProfileImage();
  const fileInput = useRef<HTMLInputElement>(null);

  // The photo being saved, shown in the big avatar until it's done.
  const [saving, setSaving] = useState<string>();
  // A sign-in photo the pointer is over, previewed in the big avatar.
  const [hovered, setHovered] = useState<string>();
  // Sign-in photos that don't load (e.g. a removed account) aren't offered.
  const [brokenUrls, setBrokenUrls] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);

  // The photo to preview over the current one, if any. The last one stays
  // mounted after the preview ends, so it can fade out instead of vanishing.
  const preview = saving ?? hovered;
  const [lastPreview, setLastPreview] = useState(preview);
  if (preview && preview !== lastPreview) {
    setLastPreview(preview);
  }

  // Free the picked file's preview when it's replaced or the modal closes.
  useEffect(
    () => () => {
      if (saving?.startsWith('blob:')) {
        URL.revokeObjectURL(saving);
      }
    },
    [saving],
  );

  if (!user) {
    return null;
  }

  const hasPhoto = !!user.profileImage;
  const busy = setPhoto.isPending;
  const photos = providerPhotos(firebaseUser).filter(
    (photo) => !brokenUrls.includes(photo.url),
  );

  const save = (source: ProfileImageSource, previewUrl?: string) => {
    setSaving(previewUrl);
    setPhoto.mutate(source, {
      onSuccess: closeModal,
      onError: () => setSaving(undefined),
    });
  };

  const saveFile = (file?: File) => {
    if (file && !busy) {
      save(file, URL.createObjectURL(file));
    }
  };

  const onFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear it so picking the same file again still fires a change.
    event.target.value = '';
    saveFile(file);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    saveFile(event.dataTransfer.files?.[0]);
  };

  const pickFile = () => fileInput.current?.click();

  const errorMessage = setPhoto.error
    ? setPhoto.error instanceof UnsupportedImageError
      ? t('profilePhoto.errorUnsupported')
      : t('profilePhoto.errorSave')
    : undefined;

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.banner} aria-hidden />
          <button
            type="button"
            onClick={closeModal}
            aria-label={t('common.close')}
            className={styles.close}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <div className={styles.hero.container}>
            <div className={styles.hero.avatarRing}>
              <MediaAvatar
                size="2xl"
                mediaId={user.profileImage?.id}
                initials={user.getInitials()}
                colorKey={user.id}
                alt=""
              />
              <span
                className={classNames(
                  styles.hero.preview,
                  preview ? 'opacity-100' : 'opacity-0',
                )}
              >
                {lastPreview && (
                  <Avatar
                    size="2xl"
                    src={lastPreview}
                    // A picked file has a local address the optimiser can't
                    // fetch.
                    unoptimized={lastPreview.startsWith('blob:')}
                    alt=""
                  />
                )}
              </span>
              {busy && (
                <span className={styles.hero.busy}>
                  <Spinner className="h-6 w-6" />
                </span>
              )}
              <button
                type="button"
                onClick={pickFile}
                disabled={busy}
                aria-label={t('profilePhoto.upload')}
                className={styles.hero.camera}
              >
                <FontAwesomeIcon icon={faCamera} />
              </button>
            </div>

            <DialogTitle as="h2" className={styles.hero.title}>
              {hasPhoto
                ? t('profilePhoto.titleChange')
                : t('profilePhoto.titleAdd')}
            </DialogTitle>
            <p className={styles.hero.subtitle}>{t('profilePhoto.subtitle')}</p>
          </div>

          <div className={styles.body}>
            {errorMessage && (
              <p role="alert" className={styles.error}>
                {errorMessage}
              </p>
            )}

            {photos.map((photo) => {
              const Logo = PROVIDER_LOGOS[photo.providerId];
              return (
                <button
                  key={photo.providerId}
                  type="button"
                  disabled={busy}
                  onClick={() => save(optimisedPhotoUrl(photo.url), photo.url)}
                  onMouseEnter={() => setHovered(photo.url)}
                  onMouseLeave={() => setHovered(undefined)}
                  className={styles.provider.card}
                >
                  <span className={styles.provider.logo}>
                    <Logo className="h-5 w-5" />
                  </span>
                  <span className={styles.provider.text}>
                    <span className={styles.provider.title}>
                      {t('profilePhoto.useProviderPhoto', {
                        provider: photo.name,
                      })}
                    </span>
                    <span className={styles.provider.hint}>
                      {t('profilePhoto.providerPhotoHint', {
                        provider: photo.name,
                      })}
                    </span>
                  </span>
                  <Avatar
                    size="sm"
                    src={photo.url}
                    alt=""
                    onImageError={() =>
                      setBrokenUrls((urls) => [...urls, photo.url])
                    }
                  />
                </button>
              );
            })}

            {photos.length > 0 && (
              <div className={styles.divider}>
                <span>{t('profilePhoto.or')}</span>
              </div>
            )}

            <button
              type="button"
              disabled={busy}
              onClick={pickFile}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={classNames(
                styles.drop.zone,
                dragging && styles.drop.dragging,
              )}
            >
              <span className={styles.drop.icon}>
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
              </span>
              <span className={styles.drop.title}>
                {t('profilePhoto.upload')}
              </span>
              <span className={styles.drop.hint}>
                {t('profilePhoto.dropHint')}
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={onFilePicked}
            />

            {hasPhoto && (
              <button
                type="button"
                disabled={busy}
                onClick={() => save(null)}
                className={styles.remove}
              >
                {t('profilePhoto.remove')}
              </button>
            )}
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

export default ProfilePhotoModal;

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-3 sm:p-4',
  panel: classNames(
    'relative flex w-full max-w-[400px] flex-col overflow-hidden rounded-3xl bg-surface-card text-ink-primary shadow-2xl',
    'transition-[opacity,scale] data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  // Brand blue with a soft light in one corner. Brand chrome stays the same
  // in light and dark mode.
  banner: classNames(
    'absolute inset-x-0 top-0 h-28',
    'bg-[radial-gradient(120%_140%_at_85%_0%,rgba(105,168,255,0.55),transparent_60%),linear-gradient(135deg,#133775,#2757BA)]',
  ),
  close:
    'absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25',
  hero: {
    container: 'relative flex flex-col items-center px-6 pt-14 text-center',
    // A ring in the card's colour, so the avatar sits cut out of the banner.
    // A flex box, not a text line, so its height doesn't change when a photo
    // replaces the initials.
    avatarRing: 'relative flex rounded-full bg-surface-card p-1.5 shadow-lg',
    // Fades the previewed photo in over the current one, and out again.
    preview:
      'pointer-events-none absolute inset-1.5 flex transition-opacity duration-200 ease-out',
    busy: 'absolute inset-1.5 flex items-center justify-center rounded-full bg-black/45 text-white',
    camera: classNames(
      'absolute bottom-0.5 right-0.5 flex h-8 w-8 items-center justify-center rounded-full',
      'border-2 border-surface-card bg-accent-fill text-xs text-white shadow-md',
      'transition-transform hover:scale-110 disabled:opacity-50',
    ),
    title: 'mt-4 text-lg font-bold text-ink-primary',
    subtitle: 'mt-1 max-w-[280px] text-balance text-sm text-ink-secondary',
  },
  body: 'flex flex-col gap-3 px-5 pt-5 pb-5',
  error:
    'rounded-xl bg-red-500/10 px-3 py-2 text-center text-xs text-ink-secondary',
  provider: {
    card: classNames(
      'group flex w-full items-center gap-3 rounded-2xl border border-stroke bg-surface-card p-3 text-left',
      'shadow-sm transition-[translate,box-shadow] hover:-translate-y-0.5 hover:border-accent-ink/40 hover:shadow-md',
      'disabled:pointer-events-none disabled:opacity-50',
    ),
    logo: 'flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5',
    text: 'flex min-w-0 flex-1 flex-col',
    title: 'text-sm font-semibold text-ink-primary',
    hint: 'truncate text-xs text-ink-tertiary',
  },
  divider: classNames(
    'flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-ink-tertiary',
    'before:h-px before:flex-1 before:bg-hairline after:h-px after:flex-1 after:bg-hairline',
  ),
  drop: {
    zone: classNames(
      'flex w-full flex-col items-center gap-1 rounded-2xl border-2 border-dashed border-stroke px-4 py-5',
      'text-center hover:border-accent-ink/50 hover:bg-accent-tint/50',
      'disabled:pointer-events-none disabled:opacity-50',
    ),
    dragging: 'border-accent-ink bg-accent-tint',
    icon: 'mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-accent-tint text-accent-ink',
    title: 'text-sm font-semibold text-ink-primary',
    hint: 'text-xs text-ink-tertiary',
  },
  remove:
    'self-center rounded-full px-3 py-1 text-xs font-semibold text-mf-red-600 hover:bg-mf-red-500/10 disabled:opacity-50 dark:text-mf-red-400',
};
